import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService, T as TrackingService } from './index3.js';
import { T as TranslationService } from './index6.js';
import { C as CookieService } from './index11.js';
import { M as MessageType, d as defineCustomElement$1 } from './roche-message2.js';
import { b as ButtonType, a as ButtonAppearance, d as defineCustomElement$4 } from './roche-button2.js';
import { r as remCalc } from './index5.js';
import { A as AdobeTracking } from './index7.js';
import { d as defineCustomElement$3 } from './roche-generic-link2.js';
import { d as defineCustomElement$2 } from './roche-icon2.js';

const recaptchaApiSource = 'https://www.google.com/recaptcha/api.js';
const RecaptchaService = {
    getScript() {
        const script = document.querySelector(`script[src="${recaptchaApiSource}"]`);
        return (script && script.getAttribute('type') !== 'text/plain') ? script : undefined;
    },
    canBeUsed() {
        if (!RecaptchaService.getScript()) {
            // eslint-disable-next-line no-console
            console.warn(`Recaptcha script not present in page. Please add <script src="${recaptchaApiSource}" async defer></script> to your page.`);
            return false;
        }
        if (!DomService.getGlobalConfig().recaptchaKey) {
            // eslint-disable-next-line no-console
            console.error('Recaptcha key not present in page.');
            return false;
        }
        return true;
    },
    initGlobal() {
        if (!RecaptchaService.getScript()) {
            const scriptAttributes = {
                src: recaptchaApiSource,
                type: CookieService.getConsent().targeting ? 'text/javascript' : 'text/plain',
                class: 'optanon-category-C0004',
                async: '',
                defer: '',
                onload: 'window.dispatchEvent(new CustomEvent("rocheRecaptchaAvailable"));console.info("Recaptcha script has loaded and run")',
            };
            document.head.appendChild(DomService.createElement('script', '', scriptAttributes));
        }
    },
    initComponent(element, id, className = '', replace = false) {
        if (!RecaptchaService.canBeUsed()) {
            return false;
        }
        // Manually create slot for the recaptcha as it cannot be on the shadowRoot,
        // otherwise the recaptcha code from Google will not render it correctly.
        if (replace) {
            // eslint-disable-next-line no-param-reassign
            element.innerHTML = `<div class="${className}" id="${id}"></div>`;
        }
        else {
            element.appendChild(DomService.createElement('div', '', { class: className, id }));
        }
        return true;
    },
    renderRecaptcha(element, callback, errorCallback) {
        const windowRecaptcha = window;
        const params = {
            sitekey: DomService.getGlobalConfig().recaptchaKey,
            callback,
            'error-callback': errorCallback,
        };
        return new Promise((resolve) => {
            const render = () => {
                if (windowRecaptcha.grecaptcha && windowRecaptcha.grecaptcha.render) {
                    windowRecaptcha.grecaptcha.render(element, params);
                    resolve();
                }
                else {
                    setTimeout(render, 100);
                }
            };
            render();
        });
    },
};

var ServerResponse;
(function (ServerResponse) {
    ServerResponse[ServerResponse["IDLE"] = 0] = "IDLE";
    ServerResponse[ServerResponse["SUCCESS"] = 1] = "SUCCESS";
    ServerResponse[ServerResponse["ERROR"] = 2] = "ERROR";
})(ServerResponse || (ServerResponse = {}));

const rocheFormCss = ":host{display:block;margin-top:3rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}:host form{display:flex;flex-direction:column}:host .actions{order:2}:host .no-recaptcha .title{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;margin-top:2rem}@media (min-width: 48rem){:host .no-recaptcha .title{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}@media (min-width: 90rem){:host .no-recaptcha .title{margin-top:2rem}}:host .no-recaptcha .link{cursor:pointer}:host roche-button{width:17.5rem;margin-top:2rem}@media (min-width: 90rem){:host roche-button{margin-top:2rem}}::slotted(.roche-form__recaptcha){order:1;display:flex;margin-top:1.5rem}::slotted(roche-richtext){margin-top:1.5rem !important}::slotted(*:nth-child(1)){margin-top:0}";
const RocheFormStyle0 = rocheFormCss;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const showOnetrustSettings = () => window.OneTrust.ToggleInfoDisplay();
const RocheForm = /*@__PURE__*/ proxyCustomElement(class RocheForm extends HTMLElement {
    get valid() {
        const hasValidInputs = Object.keys(this.validity).every((key) => this.validity[key].valid);
        return this.disableRecaptcha
            ? hasValidInputs
            : (this.validRecaptcha && hasValidInputs);
    }
    get values() {
        return Object.keys(this.validity).reduce((accumulator, key) => (Object.assign(Object.assign({}, accumulator), { [key]: this.validity[key].component.value })), this.validRecaptcha ? {
            google_recaptcha_response_token: this.validRecaptcha,
        } : {});
    }
    handleRecaptchaAvailable() {
        if (!this.disableRecaptcha
            && RecaptchaService.initComponent(this.element, this.recaptchaId, 'roche-form__recaptcha')) {
            this.recaptchaInitialized = true;
            this.validRecaptcha = false;
        }
    }
    handleChange({ detail }) {
        if (this.didFormLoad && !this.isFormDirty) {
            this.isFormDirty = true;
            AdobeTracking(this.defaultAdobeTracking, 'formStart');
        }
        this.validity = Object.assign(Object.assign({}, this.validity), { [detail.name || detail.uid]: {
                component: detail,
                valid: detail.valid,
            } });
    }
    get actionParsed() {
        return this.action || DomService.getGlobalConfig().formsApiUrl;
    }
    get components() {
        if (!this.element) {
            return [];
        }
        return Array.from(this.element.children)
            .flatMap((el) => [el, ...Array.from(el.children)])
            .filter((el) => el instanceof HTMLElement);
    }
    get activeFields() {
        return this.components.filter((field) => !field.disabled);
    }
    get recaptchaElement() {
        return this.element.querySelector(`#${this.recaptchaId}`);
    }
    get formData() {
        return JSON.stringify(Object.assign({ page_id: this.pageId, form_id: this.uid, data: this.values }, (!!this.type && {
            form_type: this.type,
        })));
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            component_name: 'roche-form',
            event: 'form_submit',
        };
    }
    get defaultAdobeTracking() {
        return {
            name: '',
            type: this.type,
        };
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.submitEvent = createEvent(this, "rocheFormSubmit", 7);
        this.uid = undefined;
        this.action = undefined;
        this.submit = undefined;
        this.submitIcon = undefined;
        this.success = undefined;
        this.successMessage = undefined;
        this.error = undefined;
        this.errorMessage = undefined;
        this.type = '';
        this.disabled = false;
        this.disableRecaptcha = false;
        this.disableSubmit = false;
        this.validity = {};
        this.validRecaptcha = undefined;
        this.recaptchaInitialized = false;
        this.sending = false;
        this.response = ServerResponse.IDLE;
        this.isFormDirty = false;
        this.didFormLoad = false;
        this.enabledFieldsAtSubmitTime = [];
        this.translations = undefined;
        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleRecaptcha = this.handleRecaptcha.bind(this);
        this.handleRecaptchaError = this.handleRecaptchaError.bind(this);
    }
    async componentWillLoad() {
        this.pageId = DomService.getGlobalConfig().pageId;
        this.recaptchaId = `roche-form-${this.uid || StringService.generate()}-recaptcha`;
        this.validRecaptcha = !RecaptchaService.canBeUsed() ? undefined : false;
        this.translations = {
            recaptchaTitle: await TranslationService.translate('confirm you are a human'),
            recaptchaCookieConsent: await TranslationService.translate('the test requires you to consent to targeting cookies'),
        };
    }
    async componentDidLoad() {
        if (!this.disableRecaptcha) {
            RecaptchaService.initGlobal();
        }
        this.components.forEach((component) => {
            component.dispatchEvent(new CustomEvent('rocheFormControlInit', { bubbles: false, detail: this }));
        });
        this.didFormLoad = true;
    }
    async componentDidRender() {
        this.updateChildren();
        if (!this.disableRecaptcha && this.recaptchaInitialized && !this.recaptchaElement.innerHTML) {
            await RecaptchaService.renderRecaptcha(this.recaptchaElement, this.handleRecaptcha, this.handleRecaptchaError);
        }
    }
    render() {
        return (h(Host, { key: '437437d1d642a06c079ece825ed4223986678b0a' }, h("form", { key: 'a56cbb900b4496a5faa23a44b42b19035da8320e', action: this.actionParsed, onSubmit: this.handleSubmit }, h("slot", { key: '77587d77be277e980afe468904a953e237993529' }), h("div", { key: '65cb80f918b31f657a34c4b12d3aeb31a4485cde', class: "no-recaptcha" }, !this.disableRecaptcha && !this.recaptchaInitialized && (h("div", null, h("div", { class: "title" }, StringService.capitalize(this.translations.recaptchaTitle), " *"), h("roche-generic-link", { class: "link", underline: false, icon: "chevron-right", text: StringService.capitalize(this.translations.recaptchaCookieConsent), onClick: showOnetrustSettings, "show-onetrust-settings": true })))), h("div", { key: 'f3e0593d3dc2b9a4d4aad15b4cfb4983d0a32c1a', class: "actions" }, h("roche-button", { key: '69ce0ecf6af72e71e542eb5b83aa55c4261c6ab7', type: ButtonType.SUBMIT, disabled: !this.valid || this.disabled, text: this.handleSubmitButton(), leftIcon: this.submitIcon, onRocheButtonClick: this.handleSubmit, ref: (el) => { this.buttonElement = el; } }), this.response === ServerResponse.ERROR && (h("roche-message", { text: this.errorMessage, type: MessageType.INLINE_FAIL })), this.response === ServerResponse.SUCCESS && (h("slot", { name: "success-message" }))))));
    }
    async handleSubmit(event) {
        event.stopPropagation();
        event.preventDefault();
        if (!this.disableSubmit) {
            this.enabledFieldsAtSubmitTime = this.activeFields;
            this.sending = true;
            this.response = ServerResponse.IDLE;
            const response = await this.submitForm();
            this.response = response && response.status >= 200 && response.status < 300
                ? ServerResponse.SUCCESS
                : ServerResponse.ERROR;
            this.sending = false;
            if (this.response === ServerResponse.SUCCESS) {
                AdobeTracking(this.defaultAdobeTracking, 'formSuccess');
            }
            if (this.response === ServerResponse.ERROR) {
                AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { error: response.status }), 'formError');
            }
            TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { value: ServerResponse[this.response].toLowerCase() }));
        }
        this.submitEvent.emit(this);
    }
    handleSubmitButton() {
        if (this.sending) {
            this.element.setAttribute('submit-icon', '');
            return h("roche-icon", { icon: "loading", height: `${remCalc(20)}rem`, class: "loading-spinner" });
        }
        if (this.response === ServerResponse.ERROR) {
            this.buttonElement.setAttribute('appearance', ButtonAppearance.ERROR);
            this.element.setAttribute('submit-icon', 'close');
            return this.error;
        }
        if (this.response === ServerResponse.SUCCESS) {
            this.buttonElement.setAttribute('appearance', ButtonAppearance.SUCCESS);
            this.element.setAttribute('submit-icon', 'checkmark');
            return this.success;
        }
        return this.submit;
    }
    updateChildren() {
        if (this.sending || this.disabled) {
            this.components.forEach((component) => component.setAttribute('disabled', ''));
            return;
        }
        this.enabledFieldsAtSubmitTime.forEach((component) => component.removeAttribute('disabled'));
    }
    handleRecaptcha(uvresp) {
        if (uvresp) {
            this.validRecaptcha = uvresp;
        }
    }
    handleRecaptchaError() {
        this.validRecaptcha = false;
    }
    async submitForm() {
        try {
            // eslint-disable-next-line compat/compat
            return fetch(this.actionParsed, {
                method: 'POST',
                cache: 'no-cache',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: this.formData,
            }).catch(() => null);
        }
        catch (_) {
            return null;
        }
    }
    get element() { return this; }
    static get style() { return RocheFormStyle0; }
}, [1, "roche-form", {
        "uid": [1],
        "action": [1],
        "submit": [1],
        "submitIcon": [1, "submit-icon"],
        "success": [1],
        "successMessage": [8, "success-message"],
        "error": [1],
        "errorMessage": [1, "error-message"],
        "type": [1],
        "disabled": [4],
        "disableRecaptcha": [4, "disable-recaptcha"],
        "disableSubmit": [4, "disable-submit"],
        "validity": [32],
        "validRecaptcha": [32],
        "recaptchaInitialized": [32],
        "sending": [32],
        "response": [32],
        "isFormDirty": [32],
        "didFormLoad": [32],
        "enabledFieldsAtSubmitTime": [32],
        "translations": [32]
    }, [[8, "rocheRecaptchaAvailable", "handleRecaptchaAvailable"], [0, "rocheFormControlChange", "handleChange"]]]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-form", "roche-button", "roche-generic-link", "roche-icon", "roche-message"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-form":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheForm);
            }
            break;
        case "roche-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-generic-link":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "roche-message":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { RocheForm as R, defineCustomElement as d };

//# sourceMappingURL=roche-form2.js.map